/**
 * @file Component: SoundControls
 * @description UI controls for adjusting sound settings
 */

import type React from 'react';
import { useAudioContext } from '../../contexts/AudioContext';
import { analytics } from '../../services/analyticsService';

interface SoundControlsProps {
  /** Optional class name for styling */
  className?: string;
}

/**
 * Sound controls component for adjusting volume and toggling sounds
 */
const SoundControls: React.FC<SoundControlsProps> = ({ className = '' }) => {
  const {
    isMusicEnabled,
    isSfxEnabled,
    musicVolume,
    sfxVolume,
    toggleMusic,
    toggleSfx,
    setMusicVolume,
    setSfxVolume,
  } = useAudioContext();

  return (
    <div className={`sound-controls ${className}`}>
      <div className="sound-control-group mb-4">
        <label className="flex items-center mb-2">
          <input
            type="checkbox"
            checked={isMusicEnabled}
            onChange={() => {
              toggleMusic();
              // Track music toggle
              analytics.trackButtonClick(
                isMusicEnabled ? 'Music Mute' : 'Music Unmute',
                'music-toggle',
              );
              analytics.trackSoundMute(!isMusicEnabled);
            }}
            className="mr-2 h-4 w-4"
          />
          <span className="text-white font-lekton">Music</span>
        </label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={musicVolume}
          onChange={(e) => setMusicVolume(parseFloat(e.target.value))}
          disabled={!isMusicEnabled}
          className="w-full accent-blue-500"
        />
      </div>

      <div className="sound-control-group">
        <label className="flex items-center mb-2">
          <input
            type="checkbox"
            checked={isSfxEnabled}
            onChange={() => {
              toggleSfx();
              // Track SFX toggle
              analytics.trackButtonClick(
                isSfxEnabled ? 'SFX Mute' : 'SFX Unmute',
                'sfx-toggle',
              );
            }}
            className="mr-2 h-4 w-4"
          />
          <span className="text-white font-lekton">Sound Effects</span>
        </label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={sfxVolume}
          onChange={(e) => setSfxVolume(parseFloat(e.target.value))}
          disabled={!isSfxEnabled}
          className="w-full accent-blue-500"
        />
      </div>
    </div>
  );
};

export default SoundControls;
